import React, { useRef, useEffect, useState, useMemo } from "react";
import type { ChangeEvent, UIEvent } from "react";
import {
   Panel,
   useI18nContext,
   Search,
   Slider,
   Spinner,
   Typography,
   Dropdown,
   EmptyState,
   Button,
   Tooltip,
} from "@procore/core-react";
import { Location, Info } from "@procore/core-icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Assignment2Store } from "@/stores/assignment-2-store.core";
import { useGroupContext } from "@/react/providers/group-context-provider";
import { getDetachedDay } from "@laborchart-modules/common/dist/datetime/date";
import { authManager } from "@/lib/managers/auth-manager";
import { ResourceCard } from "./resource-card";
import "./bench-panel.css";
import { BoardsStore } from "@/stores/boards-store.core";
import { ErrorBanner } from "../error-banner";
import { BenchFilters } from "./bench-filters";
import { ProcoreStore } from "@/stores/procore-store.core";
import { benchSortFieldMaps, CardSortField } from "./types";
import LaunchDarklyClient from "@laborchart-modules/launch-darkly-browser";

interface BenchPanelProps {
   isOpen: boolean;
   onClose: () => void;
   onResetSmartBench?: () => void;
   showJobTitles: boolean;
   showTags: boolean;
   selectedDay: number;
   selectedRequest?: {
      id: string;
      project_id: string;
   } | null;
}

interface ProximityFilterParams {
   /** Distance in miles or km depending on user locale */
   proximity_cutoff: number;
}

async function fetchBenchResources(
   searchTerm: string | undefined,
   page: number = 0,
   sortBy: CardSortField = CardSortField.JOB_TITLE,
   groupId: string,
   selectedDay?: number,
   selectedRequest?: {
      id: string;
      project_id: string;
   } | null,
   selectedJobTitleIds?: string[],
   selectedTagIds?: string[],
   proximityFilter?: ProximityFilterParams,
) {
   if (selectedRequest) {
      const params = {
         project_id: selectedRequest.project_id,
         request_id: selectedRequest.id,
         page: page + 1, // Smart bench service uses one based pagination
         page_size: 50,
         ...(proximityFilter ? { proximity_cutoff: proximityFilter.proximity_cutoff } : {}),
      };
      const response = await BoardsStore.getSmartBenchSuggestions(params);
      return response.payload;
   } else {
      const sortField = benchSortFieldMaps[sortBy];

      const groupIdParam = groupId === "my-groups" ? undefined : groupId;

      const filters: any = {};
      if (selectedJobTitleIds && selectedJobTitleIds.length > 0) {
         filters.job_title_ids = selectedJobTitleIds.map((id) => ({
            property: "position_id",
            type: "select",
            value: id,
            negation: false,
         }));
      }
      if (selectedTagIds && selectedTagIds.length > 0) {
         filters.resource_tag_ids = selectedTagIds.map((id) => ({
            property: "tag_instances",
            type: "select",
            value: [id],
            negation: false,
         }));
      }

      const options = {
         context: "available",
         dayFilter: selectedDay || getDetachedDay(new Date()),
         filters: Object.keys(filters).length > 0 ? filters : {},
         limit: 50,
         group_id: groupIdParam,
         options: {
            last_names_first:
               authManager.authedUser()?.preferences()?.displayLastNamesFirst() ?? false,
         },
         ...(searchTerm && searchTerm.trim() !== "" ? { search: searchTerm.trim() } : {}),
         skip: page * 50,
         sort: sortField,
      };

      const response = await Assignment2Store.getContextedResourceCards(options).payload;

      if (searchTerm && searchTerm.trim() && response.data.data.length === 0) {
         ProcoreStore.logAnalyticsEvent({
            key: "company.workforce_planning.bench.no_search_results",
            properties: {},
         });
      }

      return response;
   }
}

const KM_TO_MILES = 0.621371;

const DEFAULT_PROXIMITY_RADIUS = 50;
const MIN_PROXIMITY_RADIUS = 1;
const MAX_PROXIMITY_RADIUS = 150;

/**
 * Distance unit for Smart Bench UI + API conversion.
 * Keep aligned with `supportedLocales` in `src/client/translations.ts` when adding locales.
 * Miles: US + UK English; all other supported locales use km. Language keys support unknown regions (e.g. `de-CH` → `de`).
 * Locales not in this map fall back to miles (same as default `en`).
 */
const LOCALE_UNIT_MAP: Record<string, "miles" | "km"> = {
   de: "km",
   "de-DE": "km",
   en: "miles",
   "en-AU": "km",
   "en-CA": "km",
   "en-GB": "miles",
   "en-NZ": "km",
   "en-US": "miles",
   es: "km",
   "es-ES": "km",
   "es-LA": "km",
   fr: "km",
   "fr-CA": "km",
   "fr-FR": "km",
   is: "km",
   "is-IS": "km",
   it: "km",
   "it-IT": "km",
   ja: "km",
   "ja-JP": "km",
   pl: "km",
   "pl-PL": "km",
   pt: "km",
   "pt-BR": "km",
   "pt-PT": "km",
   pseudo: "miles",
   th: "km",
   "th-TH": "km",
   zh: "km",
   "zh-SG": "km",
   "zh-TW": "km",
};

function distanceUnitForLocale(locale: string | undefined): "miles" | "km" {
   const key = locale?.trim() || "en";
   const direct = LOCALE_UNIT_MAP[key];
   if (direct !== undefined) {
      return direct;
   }
   const language = key.split("-")[0];
   const byLanguage = LOCALE_UNIT_MAP[language];
   if (byLanguage !== undefined) {
      return byLanguage;
   }
   return "miles";
}

/** Converts a display value to miles for the Smart Bench API.
 *  If the user's locale uses km, converts km → miles; otherwise returns as is. */
const toMiles = (value: number, isMetric: boolean): number =>
   isMetric ? Math.round(value * KM_TO_MILES) : value;

export function BenchPanel({
   isOpen,
   onClose,
   onResetSmartBench,
   showJobTitles = true,
   showTags = true,
   selectedDay,
   selectedRequest,
}: BenchPanelProps) {
   const I18n = useI18nContext();
   const { groupId } = useGroupContext();
   const queryClient = useQueryClient();
   const draggedResourceRef = useRef<string | null>(null);
   const lastDraggedResourceRef = useRef<string | null>(null);
   const scrollContainerRef = useRef<HTMLDivElement>(null);
   const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
   const [inputValue, setInputValue] = useState("");
   const [currentPage, setCurrentPage] = useState(0);
   const [allResources, setAllResources] = useState<any[]>([]);
   const [hasNextPage, setHasNextPage] = useState(true);
   const [totalCount, setTotalCount] = useState(0);
   const [isLoadingMore, setIsLoadingMore] = useState(false);
   const [activeTab] = useState<"people" | "equipment">("people");
   const [sortBy, setSortBy] = useState<CardSortField>(CardSortField.JOB_TITLE);
   const [selectedResourceIds, setSelectedResourceIds] = useState<Set<string>>(new Set());
   const [selectedJobTitles, setSelectedJobTitles] = useState<Array<{ id: string; label: string }>>(
      [],
   );
   const [selectedTags, setSelectedTags] = useState<Array<{ id: string; label: string }>>([]);
   const isSmartBench = !!selectedRequest;

   const isProximityFilterEnabled = useMemo(
      () => LaunchDarklyClient.getFlagValue("smart-bench-proximity-filter"),
      [],
   );

   const unit = distanceUnitForLocale(I18n.locale);
   const isMetricLocale = unit === "km";

   const radiusFilterI18nBase = "views.company.workforce_planning.smart_bench.radius_filter";
   const radiusUnitSuffix = isMetricLocale ? "km" : "miles";
   const radiusLabelI18nKey = `${radiusFilterI18nBase}.label_${radiusUnitSuffix}`;
   const radiusValueI18nKey = `${radiusFilterI18nBase}.value_${radiusUnitSuffix}`;

   // Proximity radius slider — display value updates live, committed value triggers API refetch after debounce
   const [sliderDisplayValue, setSliderDisplayValue] = useState(DEFAULT_PROXIMITY_RADIUS);
   const [proximityRadius, setProximityRadius] = useState(DEFAULT_PROXIMITY_RADIUS);
   const isMountedRef = useRef(false);

   // Debounce slider changes
   // Skip on initial mount to avoid clearing resources before they are first loaded.
   useEffect(() => {
      if (!isMountedRef.current) {
         isMountedRef.current = true;
         return;
      }
      const timer = setTimeout(() => {
         setProximityRadius(sliderDisplayValue);
         setCurrentPage(0);
         setAllResources([]);
      }, 500);
      return () => clearTimeout(timer);
   }, [sliderDisplayValue]);

   const selectedJobTitleIds = useMemo(
      () => selectedJobTitles.map((jobTitle) => jobTitle.id),
      [selectedJobTitles],
   );
   const selectedTagIds = useMemo(() => selectedTags.map((tag) => tag.id), [selectedTags]);

   const { data, isLoading, error } = useQuery({
      queryKey: [
         "benchResources",
         groupId,
         searchTerm,
         currentPage,
         sortBy,
         selectedDay,
         selectedRequest,
         selectedJobTitleIds,
         selectedTagIds,
         isProximityFilterEnabled ? proximityRadius : undefined,
      ],
      queryFn: () =>
         fetchBenchResources(
            searchTerm,
            currentPage,
            sortBy,
            groupId,
            selectedDay,
            selectedRequest,
            selectedJobTitleIds,
            selectedTagIds,
            isSmartBench && isProximityFilterEnabled
               ? { proximity_cutoff: toMiles(proximityRadius, isMetricLocale) }
               : undefined,
         ),
      enabled: isOpen && !!groupId,
   });

   useEffect(() => {
      if (selectedRequest && isOpen) {
         ProcoreStore.logAnalyticsEvent({
            key: "company.workforce_planning.bench.smart_bench_initiated",
            properties: {},
         });
      }
   }, [selectedRequest, isOpen]);

   useEffect(() => {
      setAllResources([]);
      setSliderDisplayValue(DEFAULT_PROXIMITY_RADIUS);
      setProximityRadius(DEFAULT_PROXIMITY_RADIUS);
      setCurrentPage(0);
   }, [selectedRequest]);

   // Handle data updates when query succeeds
   useEffect(() => {
      if (data) {
         // Try different possible data paths
         let newResources: any[] = [];
         if (data?.data?.data && Array.isArray(data.data.data)) {
            newResources = data.data.data;
         } else if (data?.data && Array.isArray(data.data)) {
            newResources = data.data;
         } else if (Array.isArray(data)) {
            newResources = data;
         } else {
            newResources = [];
         }

         if (currentPage === 0) {
            // First page or new search/sort - replace all resources
            setAllResources(newResources);
         } else {
            // Subsequent pages - append to existing resources
            setAllResources((prev) => [...prev, ...newResources]);
         }

         setHasNextPage(newResources.length === 50);
         setTotalCount(data.data.total_count);
         setIsLoadingMore(false);
      }
   }, [data, currentPage]);

   // Handle scroll to bottom for pagination
   const handleScroll = (e: UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
      const isScrolledDown = scrollTop > 10; // 10px threshold from top

      // Update visual scroll indicators
      const element = e.currentTarget;
      if (isScrolledDown) {
         element.classList.add("scrolled-down");
      } else {
         element.classList.remove("scrolled-down");
      }

      if (isAtBottom) {
         element.classList.add("scrolled-to-bottom");
      } else {
         element.classList.remove("scrolled-to-bottom");
      }

      // Handle pagination
      if (isAtBottom && hasNextPage && !isLoadingMore && !isLoading) {
         setIsLoadingMore(true);
         setCurrentPage((prev) => prev + 1);
      }
   };

   // Reset pagination when search term, sort, selected day, or filters change
   useEffect(() => {
      setCurrentPage(0);
      setHasNextPage(true);
      // Clear selection when search/sort/day/filters change
      setSelectedResourceIds(new Set());
      // Don't reset allResources - let React Query handle cache invalidation
   }, [searchTerm, sortBy, selectedDay, selectedJobTitleIds, selectedTagIds]);

   const resources = allResources;

   // Selection handlers
   const handleResourceSelect = (resourceId: string, isSelected: boolean) => {
      setSelectedResourceIds((prev) => {
         const newSet = new Set(prev);
         if (isSelected) {
            newSet.add(resourceId);
         } else {
            newSet.delete(resourceId);
         }
         return newSet;
      });
   };

   // Get selected resources data
   const selectedResources = resources.filter((resource) =>
      selectedResourceIds.has(resource.resource_id),
   );

   // Handle successful resource assignment by mutating the cache
   const handleResourceAssigned = (resourceId: string | string[]) => {
      const resourceIds = Array.isArray(resourceId) ? resourceId : [resourceId];

      // Remove the resources from our local state immediately
      setAllResources((prev) =>
         prev.filter((resource) => !resourceIds.includes(resource.resource_id)),
      );

      // Remove assigned resources from selection
      setSelectedResourceIds((prev) => {
         const newSet = new Set(prev);
         resourceIds.forEach((id) => newSet.delete(id));
         return newSet;
      });

      // Also invalidate all related queries to ensure fresh data
      queryClient.invalidateQueries({
         queryKey: [
            "benchResources",
            groupId,
            searchTerm,
            currentPage,
            sortBy,
            selectedDay,
            selectedJobTitleIds,
            selectedTagIds,
            isProximityFilterEnabled ? proximityRadius : undefined,
         ],
         exact: false,
      });
   };

   // Effect to handle resource assignment events
   useEffect(() => {
      const handleResourceAssignedEvent = (event: CustomEvent) => {
         // Handle both single and multiple resource assignments
         const resourceId = event.detail.resourceId;
         const resourceIds = event.detail.resourceIds;

         if (resourceIds && Array.isArray(resourceIds)) {
            // Multiple resources assigned
            handleResourceAssigned(resourceIds);
         } else if (resourceId) {
            // Single resource assigned - check if it's from drag operation
            if (
               resourceId === draggedResourceRef.current ||
               resourceId === lastDraggedResourceRef.current
            ) {
               handleResourceAssigned(resourceId);
               // Clear the lastDraggedResourceRef after successful assignment
               lastDraggedResourceRef.current = null;
            }
         }
      };

      document.addEventListener("resourceAssigned", handleResourceAssignedEvent as EventListener);

      return () => {
         document.removeEventListener(
            "resourceAssigned",
            handleResourceAssignedEvent as EventListener,
         );
      };
   }, [groupId, searchTerm, sortBy, selectedDay]);

   // Handle search input change (for display only, no search triggered)
   const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setInputValue(value);
      // Don't update searchTerm here - only update display value
   };

   // Handle search submit (when user presses enter or clicks search)
   const handleSearchSubmit = (value: string) => {
      const trimmedValue = value.trim();
      setSearchTerm(trimmedValue || undefined); // This triggers the API call
      setInputValue(value); // Keep display in sync
   };

   // Handle search clear
   const handleSearchClear = () => {
      setSearchTerm(undefined);
      setInputValue("");
   };

   const handleSmartBenchReset = () => {
      onResetSmartBench?.();
   };

   return (
      <Panel
         className={`b-panel b-panel-ui-procore b-sidebar bench-panel ${
            isOpen ? "is-open" : "is-closed"
         }`}
      >
         <Panel.Header onClose={onClose}>
            <Panel.Title>{I18n.t("views.company.workforce_planning.bench.title")}</Panel.Title>
         </Panel.Header>
         <Panel.Body className="bench-panel-body">
            {!isSmartBench && (
               <>
                  {/* Search Input */}
                  <div className="bench-panel-search-input">
                     <Search
                        value={inputValue}
                        onChange={handleInputChange}
                        onSubmit={handleSearchSubmit}
                        onClear={handleSearchClear}
                        initialValue=""
                        className="bench-panel-search"
                        placeholder={I18n.t(
                           "views.company.workforce_planning.bench.search_resources",
                        )}
                     />
                     <BenchFilters
                        selectedJobTitles={selectedJobTitles}
                        selectedTags={selectedTags}
                        onJobTitlesChange={setSelectedJobTitles}
                        onTagsChange={setSelectedTags}
                     />
                  </div>
                  {/* Tabs and Filter */}
                  {/* <div
                     style={{
                        marginBottom: "16px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                     }}
                  >
                     <SegmentedController
                        aria-label="bench-tab-controller"
                        data-testid="tab-controller"
                        block={true}
                     >
                        <SegmentedController.Segment
                           onClick={() => setActiveTab("people")}
                           active={activeTab === "people"}
                           data-testid="people-tab"
                        >
                           {I18n.t("views.company.workforce_planning.bench.labor")}
                        </SegmentedController.Segment>
                        <SegmentedController.Segment
                           onClick={() => setActiveTab("equipment")}
                           active={activeTab === "equipment"}
                           data-testid="equipment-tab"
                        >
                           {I18n.t("views.company.workforce_planning.bench.equipment")}
                        </SegmentedController.Segment>
                     </SegmentedController>
                  </div> */}
                  {/* Sort Dropdown and Selection Info */}
                  <div className="bench-panel-sort-row">
                     {/* Sort dropdown */}
                     <div className="bench-panel-sort-controls">
                        <Dropdown
                           label={I18n.t("views.company.workforce_planning.bench.sort_by")}
                           variant="secondary"
                           size="sm"
                           data-testid="sort-dropdown"
                           onSelect={({ item }) => {
                              ProcoreStore.logAnalyticsEvent({
                                 key: "company.workforce_planning.bench.applied_sort",
                                 properties: {
                                    sort_by: item,
                                 },
                              });
                              setSortBy(item as CardSortField);
                           }}
                        >
                           <Dropdown.Item item={CardSortField.JOB_TITLE}>
                              {I18n.t("views.company.workforce_planning.bench.job_title")}
                           </Dropdown.Item>
                           <Dropdown.Item item={CardSortField.NAME}>
                              {I18n.t("views.company.workforce_planning.bench.name")}
                           </Dropdown.Item>
                        </Dropdown>
                     </div>
                  </div>
               </>
            )}
            {isSmartBench && (
               <div className="bench-panel-smartbench-header">
                  <Typography as="div" color="gray10">
                     {I18n.t("views.company.workforce_planning.smart_bench.count", {
                        count: totalCount,
                     })}
                  </Typography>
                  <Button onClick={handleSmartBenchReset} disabled={isLoading} variant="secondary">
                     {I18n.t("views.company.workforce_planning.smart_bench.reset")}
                  </Button>
               </div>
            )}

            {/* Proximity radius slider — only shown in Smart Bench mode when LD flag is enabled */}
            {isSmartBench && isProximityFilterEnabled && (
               <div
                  className="bench-panel-proximity"
                  data-testid="proximity-radius-slider-container"
               >
                  <div className="bench-panel-proximity-header">
                     <div className="bench-panel-proximity-title-row">
                        <Location size="sm" />
                        <Typography className="bench-panel-proximity-title">
                           {I18n.t(radiusLabelI18nKey)}
                        </Typography>
                        <Tooltip
                           overlay={
                              <Tooltip.Content>
                                 {I18n.t(
                                    "views.company.workforce_planning.smart_bench.radius_filter.tooltip",
                                 )}
                              </Tooltip.Content>
                           }
                           trigger="hover"
                        >
                           <Info size="sm" className="bench-panel-info-icon" />
                        </Tooltip>
                     </div>
                     <Typography
                        className="bench-panel-proximity-value"
                        data-testid="proximity-radius-value"
                     >
                        {I18n.t(radiusValueI18nKey, {
                           distance: sliderDisplayValue,
                           count: sliderDisplayValue,
                        })}
                     </Typography>
                  </div>
                  <Slider
                     min={MIN_PROXIMITY_RADIUS}
                     max={MAX_PROXIMITY_RADIUS}
                     step={1}
                     value={sliderDisplayValue}
                     data-testid="proximity-radius-slider"
                     aria-label={I18n.t(radiusLabelI18nKey)}
                     aria-valuemin={MIN_PROXIMITY_RADIUS}
                     aria-valuemax={MAX_PROXIMITY_RADIUS}
                     aria-valuenow={sliderDisplayValue}
                     onChange={(e) => {
                        setSliderDisplayValue(Number(e.target.value));
                     }}
                  />
               </div>
            )}

            {/* Content Area */}
            <div
               ref={scrollContainerRef}
               onScroll={handleScroll}
               className="bench-panel-scrollable unhide-scrollbar bench-panel-content"
               data-testid="people-list-container"
            >
               {activeTab === "people" ? (
                  <>
                     {isLoading && currentPage === 0 && (
                        <div className="bench-panel-loading" data-testid="loading-spinner">
                           <Spinner />
                           {isSmartBench && (
                              <Typography
                                 as="div"
                                 color="gray10"
                                 className="bench-panel-status-text"
                              >
                                 {I18n.t("views.company.workforce_planning.smart_bench.loading")}
                              </Typography>
                           )}
                        </div>
                     )}
                     {error && (
                        <ErrorBanner
                           title={I18n.t("views.company.workforce_planning.error")}
                           content={I18n.t("views.company.workforce_planning.bench.error")}
                        />
                     )}
                     {resources.length === 0 && !isLoading && !error && (
                        <EmptyState size="sm">
                           <EmptyState.NoItems />
                           <EmptyState.Title>
                              {I18n.t("views.company.workforce_planning.bench.empty")}
                           </EmptyState.Title>
                        </EmptyState>
                     )}
                     {resources.length > 0 && (
                        <div>
                           {resources.map((resource: any) => (
                              <ResourceCard
                                 key={resource.resource_id}
                                 resource={resource}
                                 showJobTitles={showJobTitles}
                                 showTags={showTags}
                                 draggedResourceRef={draggedResourceRef}
                                 lastDraggedResourceRef={lastDraggedResourceRef}
                                 isSelected={selectedResourceIds.has(resource.resource_id)}
                                 onSelectionChange={handleResourceSelect}
                                 selectedResources={selectedResources}
                                 rank={resource.rank}
                              />
                           ))}

                           {/* Loading more spinner - shown below existing cards */}
                           {isLoadingMore && (
                              <div
                                 className="bench-panel-loading-more"
                                 data-testid="loading-more-spinner"
                              >
                                 <Spinner />
                              </div>
                           )}
                        </div>
                     )}
                  </>
               ) : (
                  // Equipment tab content
                  <div className="bench-panel-loading">
                     <Typography as="div" color="gray10" className="bench-panel-status-text">
                        Equipment coming soon
                     </Typography>
                  </div>
               )}
            </div>
         </Panel.Body>
      </Panel>
   );
}
